// src/pages/HrDashboard/HrActionsTile.js
import React, { useState, useEffect, useCallback } from 'react';
import { mentoringApi as mApi } from '../../axiosInstance';

const ACTION_TYPE_LABELS = {
  invite_reminder: 'Einladungs-Reminder',
  bigfive_reminder: 'BigFive-Reminder',
  agreement_followup: 'Agreement Follow-up',
  token_renewal: 'Token erneuern',
  feedback_reminder: 'Feedback-Reminder',
  matching_confirmation: 'Matching-Bestätigung',
  kennenlerntermin: 'Kennenlerntermin',
  profile_completion: 'Profil vervollständigen',
  manual: 'Manuell',
};

const PRIORITY_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-gray-500',
};

const STATUS_COLORS = {
  open: 'text-blue-400',
  in_progress: 'text-orange-400',
  done: 'text-green-400',
  dismissed: 'text-gray-500',
};

const STATUS_LABELS = {
  open: 'Offen',
  in_progress: 'In Bearbeitung',
  done: 'Erledigt',
  dismissed: 'Verworfen',
};

const FILTER_OPTIONS = [
  { key: 'active', label: 'Aktiv' },
  { key: 'open', label: 'Offen' },
  { key: 'in_progress', label: 'In Bearbeitung' },
  { key: 'done', label: 'Erledigt' },
  { key: 'dismissed', label: 'Verworfen' },
  { key: 'all', label: 'Alle' },
];

function formatDate(dateStr) {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date(new Date().toDateString());
}

function getFilterCount(actions, key) {
  if (key === 'all') return actions.length;
  if (key === 'active') return actions.filter(a => a.status === 'open' || a.status === 'in_progress').length;
  return actions.filter(a => a.status === key).length;
}

const EMPTY_FORM = { title: '', action_type: 'manual', description: '', priority: 'medium', due_date: '' };

export default function HrActionsTile({ tenantId, selectedSurveyId }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [expandedId, setExpandedId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAction, setNewAction] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState({});

  const loadActions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await mApi.get('/hr/actions', { withCredentials: true });
      if (data.ok) setActions(data.actions || []);
      else setActions([]);
    } catch {
      setError('Fehler beim Laden der Maßnahmen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadActions(); }, [loadActions]);

  // S1.5: Auf im KI-Chat angelegte Massnahmen reagieren und neu laden.
  useEffect(() => {
    const handler = () => loadActions();
    window.addEventListener('hrActionCreated', handler);
    return () => window.removeEventListener('hrActionCreated', handler);
  }, [loadActions]);

  const filtered = actions.filter(a => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return a.status === 'open' || a.status === 'in_progress';
    return a.status === statusFilter;
  });

  async function updateStatus(actionId, newStatus) {
    setActions(prev => prev.map(a =>
      a.action_id === actionId
        ? { ...a, status: newStatus, completed_at: newStatus === 'done' ? new Date().toISOString() : a.completed_at }
        : a
    ));
    try {
      await mApi.patch(`/hr/actions/${actionId}`, { status: newStatus }, { withCredentials: true });
    } catch {
      loadActions();
    }
  }

  async function handleAppendNote(actionId) {
    const text = (newNote[actionId] || '').trim();
    if (!text) return;
    const now = new Date();
    const prefix = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.`;
    const entry = `${prefix} ${text}`;
    const action = actions.find(a => a.action_id === actionId);
    const existing = action?.notes || '';
    const updated = existing ? `${entry}\n${existing}` : entry;
    setActions(prev => prev.map(a => a.action_id === actionId ? { ...a, notes: updated } : a));
    setNewNote(prev => ({ ...prev, [actionId]: '' }));
    try {
      await mApi.patch(`/hr/actions/${actionId}`, { notes: updated }, { withCredentials: true });
    } catch {
      setError('Fehler beim Speichern');
      loadActions();
    }
  }

  async function deleteAction(actionId) {
    if (!window.confirm('Maßnahme wirklich löschen?')) return;
    try {
      await mApi.delete(`/hr/actions/${actionId}`, { withCredentials: true });
      setActions(prev => prev.filter(a => a.action_id !== actionId));
    } catch {
      setError('Fehler beim Löschen');
    }
  }

  async function createAction() {
    if (!newAction.title.trim()) return;
    setSaving(true);
    try {
      const body = {
        ...newAction,
        survey_id: selectedSurveyId || undefined,
        due_date: newAction.due_date || undefined,
      };
      await mApi.post('/hr/actions', body, { withCredentials: true });
      setShowCreateForm(false);
      setNewAction(EMPTY_FORM);
      loadActions();
    } catch {
      setError('Fehler beim Anlegen der Maßnahme');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-gray-800 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white">
          Maßnahmen{!loading && ` (${filtered.length})`}
        </h3>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="text-sm font-semibold px-3 py-1.5 rounded-xl bg-mentixxgreen text-black hover:bg-green-400 transition"
        >
          + Neue Maßnahme
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Titel *</label>
              <input
                type="text"
                value={newAction.title}
                onChange={e => setNewAction(p => ({ ...p, title: e.target.value }))}
                placeholder="Maßnahme beschreiben…"
                className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Typ</label>
              <select
                value={newAction.action_type}
                onChange={e => setNewAction(p => ({ ...p, action_type: e.target.value }))}
                className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm"
              >
                {Object.entries(ACTION_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Priorität</label>
              <select
                value={newAction.priority}
                onChange={e => setNewAction(p => ({ ...p, priority: e.target.value }))}
                className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm"
              >
                <option value="low">Niedrig</option>
                <option value="medium">Mittel</option>
                <option value="high">Hoch</option>
                <option value="critical">Kritisch</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Frist</label>
              <input
                type="date"
                value={newAction.due_date}
                onChange={e => setNewAction(p => ({ ...p, due_date: e.target.value }))}
                className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="text-xs text-gray-400 block mb-1">Beschreibung</label>
            <textarea
              value={newAction.description}
              onChange={e => setNewAction(p => ({ ...p, description: e.target.value }))}
              placeholder="Optional: Details zur Maßnahme…"
              rows={2}
              className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={createAction}
              disabled={saving || !newAction.title.trim()}
              className="text-sm font-semibold px-4 py-1.5 rounded-xl bg-mentixxgreen text-black hover:bg-green-400 transition disabled:opacity-50"
            >
              {saving ? 'Speichern…' : 'Anlegen'}
            </button>
            <button
              onClick={() => { setShowCreateForm(false); setNewAction(EMPTY_FORM); }}
              className="text-sm font-semibold px-4 py-1.5 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 transition"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-red-400 text-sm mb-3 bg-red-900/20 border border-red-800/30 rounded-xl px-3 py-2">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTER_OPTIONS.map(f => {
          const count = getFilterCount(actions, f.key);
          const active = statusFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-xl transition ${
                active
                  ? 'bg-mentixxgreen text-black'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {f.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-gray-400 text-sm py-4 text-center">Lade Maßnahmen…</div>
      )}

      {/* Action List */}
      {!loading && filtered.length === 0 && (
        <div className="bg-gray-800/50 border border-gray-700/30 rounded-xl p-8 text-center text-gray-400">
          Keine Maßnahmen vorhanden.
          <br />
          <span className="text-sm">Nutzen Sie den KI-Assistenten, um Handlungsbedarf zu identifizieren.</span>
        </div>
      )}

      {!loading && filtered.map(action => {
        const expanded = expandedId === action.action_id;
        return (
          <div key={action.action_id} className="mb-2">
            {/* Row */}
            <div
              onClick={() => setExpandedId(expanded ? null : action.action_id)}
              className="bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/30 rounded-xl p-3 cursor-pointer flex items-center gap-3 transition"
            >
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${PRIORITY_COLORS[action.priority] || 'bg-gray-500'}`} />
              <span className="flex-1 text-sm text-white font-medium truncate">{action.title}</span>
              <span className={`text-xs font-semibold ${STATUS_COLORS[action.status] || 'text-gray-400'}`}>
                {STATUS_LABELS[action.status] || action.status}
              </span>
              <span className={`text-xs ${isOverdue(action.due_date) && action.status !== 'done' && action.status !== 'dismissed' ? 'text-red-400 font-semibold' : 'text-gray-400'}`}>
                {formatDate(action.due_date)}
              </span>
              <span className="text-gray-500 text-xs">{expanded ? '▲' : '▼'}</span>
            </div>

            {/* Expanded Detail */}
            {expanded && (
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 mt-1 mb-1">
                <p className="text-gray-300 text-sm mb-3">{action.description || 'Keine Beschreibung.'}</p>

                {/* Aktivitäts-Log */}
                <div className="mb-3">
                  <label className="text-xs text-gray-400 block mb-1">Aktivitäts-Log</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newNote[action.action_id] || ''}
                      onChange={e => setNewNote(p => ({ ...p, [action.action_id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') handleAppendNote(action.action_id); }}
                      placeholder="Neuer Eintrag…"
                      className="flex-1 bg-gray-800 text-white border border-gray-700 rounded-lg px-3 py-1.5 text-sm"
                    />
                    <button
                      onClick={() => handleAppendNote(action.action_id)}
                      disabled={!(newNote[action.action_id] || '').trim()}
                      className="text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-500 transition disabled:opacity-40"
                    >
                      + Eintrag
                    </button>
                  </div>
                  {action.notes ? (
                    <pre className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-300 whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {action.notes}
                    </pre>
                  ) : (
                    <span className="text-xs text-gray-500">Noch keine Einträge.</span>
                  )}
                </div>

                {/* Meta */}
                <div className="text-xs text-gray-500 flex flex-wrap gap-4 mb-3">
                  <span>Typ: {ACTION_TYPE_LABELS[action.action_type] || action.action_type}</span>
                  <span>Erstellt: {formatDate(action.created_at)}</span>
                  {action.completed_at && <span>Erledigt: {formatDate(action.completed_at)}</span>}
                  {action.target_type === 'matching' && action.target_id && <span>Paar-ID: #{action.target_id}</span>}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {action.status !== 'done' && (
                    <button
                      onClick={() => updateStatus(action.action_id, 'done')}
                      className="text-sm bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-500 transition"
                    >
                      Als erledigt markieren
                    </button>
                  )}
                  {action.status === 'open' && (
                    <button
                      onClick={() => updateStatus(action.action_id, 'in_progress')}
                      className="text-sm bg-orange-600 text-white px-3 py-1 rounded-lg hover:bg-orange-500 transition"
                    >
                      In Bearbeitung
                    </button>
                  )}
                  {action.status !== 'dismissed' && action.status !== 'done' && (
                    <button
                      onClick={() => updateStatus(action.action_id, 'dismissed')}
                      className="text-sm bg-gray-600 text-white px-3 py-1 rounded-lg hover:bg-gray-500 transition"
                    >
                      Verwerfen
                    </button>
                  )}
                  <button
                    onClick={() => deleteAction(action.action_id)}
                    className="text-sm bg-red-600/30 text-red-400 px-3 py-1 rounded-lg hover:bg-red-600/50 transition ml-auto"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
